import { flow, Instance, types } from 'mobx-state-tree';
import type { ThreeDSecureParametersBase, VerifyCardResult } from 'yl-braintree-three-ds/Types';

import { getRootStore } from '../../../../../root/RootStoreUtils';
import { PaymentMethodProviderType } from '../../../../../../external/shared/api/EnrollmentClient.generated';

async function initialize(currencyCode: string) {
  // eslint-disable-next-line import/no-unresolved
  const { InitializeThreeDSecure } = await import('yl-braintree-three-ds/YlBraintree3ds');
  await InitializeThreeDSecure(currencyCode);
}

export const Braintree3dsStore = types
  .model({})
  .volatile(() => ({
    initPromise: undefined as Promise<void> | undefined
  }))
  .views(self => ({
    get isEnabled(): boolean {
      const { config, isProviderEnabled } = getRootStore(self).moduleStores.threeDSecureEntry!;
      return config.braintree3dsEnabled && isProviderEnabled(PaymentMethodProviderType.Braintree, true);
    }
  }))
  .actions(self => ({
    async initAuth(): Promise<void> {
      if (!self.initPromise) {
        const rootStore = getRootStore(self);
        const { currency } = rootStore.reference;
        self.initPromise = initialize(currency.code);
      }
      return self.initPromise;
    }
  }))
  .actions(self => ({
    auth: flow(function* auth(): any {
      yield self.initAuth();

      const rootStore = getRootStore(self);
      const {
        billingEntry,
        appliedPaymentsReview,
        shippingAndBillingReview,
        personalInfoReview,
        orderSummary,
        shippingAddressEntry
      } = rootStore.moduleStores;

      const { currency } = rootStore.reference;
      const { enrollmentId } = rootStore.enrollmentStatus;

      const creditCardData = billingEntry?.creditCardData ?? appliedPaymentsReview!.creditCardData;

      const billingAddress =
        billingEntry?.addressForBilling ??
        shippingAddressEntry?.primaryAddress ??
        shippingAndBillingReview!.billingAddress;
      const shippingAddress =
        billingEntry?.userShippingAddress ??
        shippingAddressEntry?.pickupPointAddress ??
        shippingAndBillingReview!.userShippingAddress;
      const email = billingEntry?.userEmail ?? personalInfoReview!.email;
      const mobilePhoneNumber = billingEntry?.userPhoneNumber ?? personalInfoReview!.primaryPhone;

      const { encryptedCardNumber, encryptedCvv } = billingEntry?.creditCardData.encryptedParameters ?? {};

      const {
        TokenProviders,
        TransactionStatus,
        VerifyCard,
        VerifyCardNewCard
      }: typeof import('yl-braintree-three-ds/YlBraintree3ds') =
        // eslint-disable-next-line import/no-unresolved
        yield import('yl-braintree-three-ds/YlBraintree3ds');

      const threeDSecureParameters: ThreeDSecureParametersBase = {
        walletId: enrollmentId,
        currencyCode: currency.code,
        bin: creditCardData.bin!,
        email,
        mobilePhoneNumber,
        billingAddress: {
          givenName: billingAddress.recipient?.split(' ')[0],
          surname: billingAddress.recipient?.split(' ')[1],
          phoneNumber: billingAddress.phoneNumber,
          streetAddress: billingAddress.street1,
          extendedAddress: billingAddress.street2,
          line3: billingAddress.street3,
          locality: billingAddress.city,
          region: billingAddress.stateProv,
          postalCode: billingAddress.postalCode,
          countryCodeAlpha2: billingAddress.country
        },
        additionalInformation: {
          shippingGivenName: shippingAddress.recipient?.split(' ')[0],
          shippingSurname: shippingAddress.recipient?.split(' ')[1],
          shippingPhone: shippingAddress.phoneNumber,
          shippingAddress: {
            streetAddress: shippingAddress.street1!,
            extendedAddress: shippingAddress.street2!,
            line3: shippingAddress.street3!,
            locality: shippingAddress.city!,
            region: shippingAddress.stateProv!,
            postalCode: shippingAddress.postalCode!,
            countryCodeAlpha2: shippingAddress.country!
          }
        }
      };

      const result: VerifyCardResult = rootStore.stepsManager.isLastStep
        ? yield VerifyCard({
            ...threeDSecureParameters,
            creditCardData: {
              tokenInfo: {
                value: creditCardData.tokenExToken!,
                provider: TokenProviders.TokenEx
              },
              expMonth: creditCardData.expirationMonth!,
              expYear: creditCardData.expirationYear!
            },
            amount: currency.toDecimalAmount(appliedPaymentsReview!.amount),
            paymentMethodId: appliedPaymentsReview!.selectedPaymentIdentifier!,
            externalReferenceId: orderSummary!.externalReferenceId,
            isSubscription: orderSummary!.isErEnrollment
          })
        : yield VerifyCardNewCard({
            ...threeDSecureParameters,
            creditCardData: {
              encryptedCreditCardNumber: encryptedCardNumber!,
              expMonth: creditCardData.expirationMonth!,
              expYear: creditCardData.expirationYear!,
              encryptedCvv
            }
          });
      const status = result?.threeDSecureInfo?.status;
      if (status !== TransactionStatus.AuthenticateSuccessful) {
        throw new Error(status);
      }
      return result.clientJsonValue;
    })
  }));

export interface Braintree3dsStore extends Instance<typeof Braintree3dsStore> {}
